import React, { useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import capitalize from 'lodash/capitalize'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { MachineActions } from '@galo-monitor/stores/machine'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import MachineModal from '@galo-monitor/web-containers/modals/farm/MachineModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Machines = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const currentProperty = useSelector(selectCurrentProperty)

  const { data: machines, mutate, isValidating } = useGaloMonitorApi('/farms/machines')

  const userActions = useMemo(
    () => getActionsBySlug(routes.machines.slug),
    [getActionsBySlug, routes.machines.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('type', { howMany: 1 }),
        field: 'type',
        render: ({ type }) => capitalize(type)
      },
      {
        title: t('brand', { howMany: 1 }),
        field: 'maker'
      },
      {
        title: t('model', { howMany: 1 }),
        field: 'model'
      },
      {
        title: t('fabrication year'),
        field: 'yearFabrication'
      },
      {
        title: `${ t('potency') } (${ t('horse power unit') })`,
        field: 'potential'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-machine',
        Component: MachineModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, machine) => {
      createDialog({
        id: 'edit-machine',
        Component: MachineModal,
        props: {
          machine,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onSoftDeleteClick = useCallback(
    (_, machine) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            action: t('inactivate'),
            this: t('machine', { howMany: 1 }),
            name: machine.name
          }),
          buttonTitle: t('inactivate'),
          onConfirm: () => {
            dispatch(MachineActions.removeMachine(
              machine,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('machine', { howMany: 1 }),
                  action: toLower(t('inactivated', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'machines-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('inactivate')),
                      this: toLower(t('machine', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.delete) {
        datatableActions.push(
          () => ({
            icon: DataTableIcons.Delete,
            tooltip: t('datagrid body inactivate tooltip'),
            onClick: onSoftDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [isValidating, onSoftDeleteClick, t, userActions.delete]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.machines.title,
      key: 'farm-machines-screen',
      url: '/farm/machines',
      active: true
    }
  ], [routes.machines.title])

  useEffect(() => {
    mutate()
  }, [currentProperty, mutate])

  return (
    <Screen title={ routes.machines.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ machines || [] }
        columns={ columns }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        customActionsAtLast
      />
    </Screen>
  )
}

export default Machines
